import { Injectable } from '@angular/core';

export type MobileActionWallType = 'play' | 'download' | 'join-eventlist';

@Injectable({
  providedIn: 'root'
})
export class VerificationContractService {
  private readonly emailSigninGateMessage =
    'Please verify your email and sign in again.';
  private readonly otpCooldownMessage =
    'Please wait 60 seconds before requesting a new OTP.';
  private readonly otpLockoutMessage =
    'Too many failed attempts. Verification locked.';
  private readonly mobileDailyLimitMessage =
    'You have reached the maximum of 5 OTP requests per day.';
  private readonly duplicateMobileMessage =
    'This mobile number is already registered with another account.';
  private readonly mobileDownloadWallMessage =
    'Mobile verification is required to download beats.';
  private readonly mobilePlayWallMessage =
    'You have reached 50 plays. Please verify your mobile number to continue.';
  private readonly signinWithOtpMissingEmailHint = 'email';
  private readonly signinWithOtpBannedHint = 'banned';
  private readonly signinWithOtpAccountNotFoundHint = 'not found';

  extractErrorMessage(error: any): string {
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.error?.details) {
      return error.error.details;
    }
    if (error?.error?.error) {
      return error.error.error;
    }
    return 'Something went wrong. Please try again.';
  }

  getStatusCode(error: any): number {
    if (typeof error?.status === 'number') {
      return error.status;
    }
    return 0;
  }

  isEmailSigninVerificationGate(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    const normalizedMessage = message.replace(/[.!]/g, '').trim();
    const expectedMessage = this.emailSigninGateMessage
      .toLowerCase()
      .replace(/[.!]/g, '')
      .trim();
    const emailGateData = this.getEmailSigninGateData(error);
    const hasUnverifiedUserData =
      !!(emailGateData?._id || emailGateData?.user?._id) &&
      (emailGateData?.is_email_verified === false ||
        emailGateData?.user?.is_email_verified === false);
    return (
      this.getStatusCode(error) === 403 &&
      (normalizedMessage.includes(expectedMessage) ||
        normalizedMessage.includes('please verify your email') ||
        hasUnverifiedUserData)
    );
  }

  getEmailSigninGateData(error: any): any {
    return error?.error?.data || null;
  }

  isOtpCooldown(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return (
      this.getStatusCode(error) === 429 &&
      (message.includes(this.otpCooldownMessage.toLowerCase()) ||
        message.includes('before requesting a new otp'))
    );
  }

  isOtpLockout(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return (
      this.getStatusCode(error) === 429 &&
      message.includes(this.otpLockoutMessage.toLowerCase())
    );
  }

  isOtpLockoutMessage(message: string): boolean {
    return !!message && message.toLowerCase().includes(this.otpLockoutMessage.toLowerCase());
  }

  isSigninWithOtpMissingEmail(error: any): boolean {
    return (
      this.getStatusCode(error) === 400 &&
      this.extractErrorMessage(error)
        .toLowerCase()
        .includes(this.signinWithOtpMissingEmailHint)
    );
  }

  isSigninWithOtpAccountNotFound(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return (
      this.getStatusCode(error) === 404 ||
      message.includes(this.signinWithOtpAccountNotFoundHint)
    );
  }

  isSigninWithOtpUserBanned(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return (
      this.getStatusCode(error) === 403 &&
      !this.isEmailSigninVerificationGate(error) &&
      message.includes(this.signinWithOtpBannedHint)
    );
  }

  isSigninWithOtpCooldown(error: any): boolean {
    return this.isOtpCooldown(error);
  }

  isMobileDailyLimit(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return (
      this.getStatusCode(error) === 429 &&
      message.includes(this.mobileDailyLimitMessage.toLowerCase())
    );
  }

  isDuplicateMobile(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return (
      this.getStatusCode(error) === 400 &&
      message.includes(this.duplicateMobileMessage.toLowerCase())
    );
  }

  isMobileActionWall(error: any): boolean {
    return this.getMobileActionWallType(error) !== null;
  }

  getMobileActionWallType(error: any): MobileActionWallType | null {
    const message = this.extractErrorMessage(error).toLowerCase();
    if (this.getStatusCode(error) !== 403) {
      return null;
    }

    if (message.includes(this.mobileDownloadWallMessage.toLowerCase())) {
      return 'download';
    }

    if (message.includes(this.mobilePlayWallMessage.toLowerCase())) {
      return 'play';
    }

    return null;
  }
}
